import { Button, Input, Modal, Space, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import recruitmentAiApi, { AIJobDescription } from 'api/recruitmentAiApi';
import AiOutputLanguageSelector, {
  AiOutputLanguage,
  getDefaultAiOutputLanguage,
} from 'features/recruitment/components/AiOutputLanguageSelector';

const { Paragraph, Text } = Typography;
const { TextArea } = Input;

export type AiJobDraft = AIJobDescription;

interface GenerateJobDescriptionModalProps {
  visible: boolean;
  initialPrompt?: string;
  onCancel: () => void;
  onGenerated: (draft: AiJobDraft, prompt: string) => void;
}

const promptSuggestions = [
  'Tuyển 2 Senior Frontend Developer, ReactJS, 3+ năm kinh nghiệm, lương 25–40 triệu.',
  'Need a Product Marketing Manager for a B2B SaaS product, hybrid working.',
  'Tuyển HR Executive mạnh về C&B, giao tiếp tốt và có 2 năm kinh nghiệm.',
];

const GenerateJobDescriptionModal = ({
  visible,
  initialPrompt = '',
  onCancel,
  onGenerated,
}: GenerateJobDescriptionModalProps) => {
  const { t, i18n } = useTranslation(['recruitment']);
  const [prompt, setPrompt] = useState(initialPrompt);
  const [outputLanguage, setOutputLanguage] = useState<AiOutputLanguage>(() =>
    getDefaultAiOutputLanguage(i18n.language),
  );
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (visible) {
      setPrompt(initialPrompt);
    }
  }, [initialPrompt, visible]);

  const handleGenerate = async () => {
    const cleanPrompt = prompt.trim();
    if (!cleanPrompt) return;

    setIsGenerating(true);
    try {
      const draft = await recruitmentAiApi.generateJobDescription({
        prompt: cleanPrompt,
        options: { language: outputLanguage },
      });
      onGenerated(draft, cleanPrompt);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <StyledModal
      visible={visible}
      width={760}
      centered
      maskClosable={false}
      destroyOnClose={false}
      onCancel={isGenerating ? undefined : onCancel}
      footer={null}
      title={t('recruitment:ai_jd_modal_title')}
    >
      <ModalDescription>{t('recruitment:ai_jd_modal_subtitle')}</ModalDescription>

      <LanguageRow>
        <AiOutputLanguageSelector
          value={outputLanguage}
          onChange={setOutputLanguage}
          disabled={isGenerating}
        />
      </LanguageRow>

      <FieldLabel>{t('recruitment:ai_jd_prompt_label')}</FieldLabel>
      <TextArea
        autoFocus
        value={prompt}
        onChange={(event) => setPrompt(event.target.value)}
        placeholder={t('recruitment:ai_jd_prompt_placeholder')}
        maxLength={1500}
        showCount
        rows={7}
        disabled={isGenerating}
      />
      <PromptTip>{t('recruitment:ai_jd_prompt_tip')}</PromptTip>

      <SuggestionSection>
        <Text type="secondary">{t('recruitment:ai_jd_try_example')}</Text>
        <SuggestionList>
          {promptSuggestions.map((suggestion) => (
            <Button
              key={suggestion}
              size="small"
              onClick={() => setPrompt(suggestion)}
              disabled={isGenerating}
            >
              {suggestion}
            </Button>
          ))}
        </SuggestionList>
      </SuggestionSection>

      <OutputDescription>
        <Text strong>{t('recruitment:ai_jd_will_create')}: </Text>
        <Text type="secondary">
          {t('recruitment:ai_jd_overview')}, {t('recruitment:ai_jd_responsibilities')},{' '}
          {t('recruitment:ai_jd_requirements')}, {t('recruitment:ai_jd_benefits')}.
        </Text>
      </OutputDescription>

      <Footer>
        <Paragraph>{t('recruitment:ai_jd_review_note')}</Paragraph>
        <Space>
          <Button onClick={onCancel} disabled={isGenerating}>
            {t('recruitment:cancel')}
          </Button>
          <GenerateButton loading={isGenerating} disabled={!prompt.trim()} onClick={handleGenerate}>
            {isGenerating
              ? t('recruitment:ai_jd_generating')
              : t('recruitment:ai_jd_generate_action')}
          </GenerateButton>
        </Space>
      </Footer>
    </StyledModal>
  );
};

export default GenerateJobDescriptionModal;

const StyledModal = styled(Modal)`
  .ant-modal-body {
    padding-top: 20px;
  }
`;

const ModalDescription = styled(Paragraph)`
  margin-bottom: 20px;
  color: var(--cl_neutral500);
`;

const LanguageRow = styled.div`
  margin-bottom: 20px;
`;

const FieldLabel = styled.div`
  margin-bottom: 8px;
  color: var(--cl_black);
  font-size: var(--txt_font_size_body);
`;

const PromptTip = styled(Paragraph)`
  margin-top: 8px;
  margin-bottom: 0;
  color: var(--cl_neutral500);
  font-size: var(--txt_font_size_caption);
`;

const SuggestionSection = styled.div`
  margin-top: 20px;
`;

const SuggestionList = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  margin-top: 9px;

  .ant-btn {
    height: auto;
    max-width: 100%;
    padding: 4px 10px;
    white-space: normal;
    text-align: left;
  }
`;

const OutputDescription = styled.div`
  margin-top: 20px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-top: 20px;
  margin-top: 20px;
  border-top: 1px solid var(--cl_neutral100);

  .ant-typography {
    max-width: 410px;
    margin: 0;
    color: var(--cl_neutral500);
    font-size: var(--txt_font_size_caption);
    line-height: 1.45;
  }
`;

const GenerateButton = styled(Button)`
  min-width: 142px;
  color: var(--cl_white);
  border-color: var(--cl_primary500);
  border-radius: var(--bd_radius_default);
  background-color: var(--cl_primary500);

  &:hover,
  &:focus {
    color: var(--cl_white);
    border-color: var(--cl_primary500);
    background-color: var(--cl_primary500);
  }
`;
